import { Button } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PreviewMapContext } from '../../../stores/TimetableVersion/PinboardUIStore';
import { dayToTranslation } from '../../../utils/dayToTranslation';
import { PreviewSectionContext, previewMapSections } from '../../../utils/previewMapSections';
import { useTimetableStore } from '../TimetableProvider';
import styles from './PreviewMap.module.scss';
import { PreviewSection } from './PreviewSection';
import { adjacentRoomCells, buildRows, cardRoomSupply, PreviewRow, teacherClassUuids } from './previewMapRows';

const CONTEXTS: PreviewMapContext[] = ['classes', 'teachers', 'rooms'];

type SectionData = {
  context: PreviewSectionContext;
  rows: PreviewRow[];
  pickedResources: Set<string>;
  pickedCells: Set<string>;
  markedCells: Map<string, 'previous' | 'next'>;
  /** Textzeilen unter der Abschnitts-Überschrift („Davor in: …“ / „Danach in: …“). */
  notes: string[];
};

/**
 * Kompakte Mini-Map des Plans. Sie kann unabhängig vom Board-Kontext Klassen, Lehrkräfte oder
 * Räume als Zeilen zeigen (Orientierung: „was passiert woanders?“). Belegte Slots sind markiert;
 * hat man eine Karte in der Hand, werden deren Zeilen im aktuellen Vorschau-Kontext hervorgehoben.
 *
 * Im Automatik-Modus (Standard) zeigt sie, sobald eine Karte in der Hand ist, mehrere Abschnitte
 * untereinander: genau die Ressourcen, die im aktuellen Board-Kontext NICHT sichtbar sind – jeweils
 * eingeschränkt auf die Ressourcen dieser Karte. Eine manuelle Kontext-Wahl schaltet die Automatik ab.
 */
export const PreviewMap: FC = observer(() => {
  const store = useTimetableStore();
  const { t } = useTranslation();

  // Statische Keys: `update:translations` erkennt keine dynamisch zusammengesetzten Keys und
  // würde sie als unbenutzt entfernen.
  const contextLabels: Record<PreviewSectionContext, string> = {
    classes: t('pinboard.previewMap.context.classes'),
    teachers: t('pinboard.previewMap.context.teachers'),
    rooms: t('pinboard.previewMap.context.rooms'),
  };

  const context = store.previewMapContext;
  const previewHours = useMemo(() => store.hours.filter((hour) => !hour.isPause), [store.hours]);

  const picked = store.pickedCard;
  const pickedCard = picked ? store.getCardByUuid(picked.cardUuid) : undefined;
  const autoActive = store.previewMapAuto && Boolean(pickedCard);

  const sections: PreviewSectionContext[] = autoActive ? previewMapSections(store.context) : [context];

  // Zeilenaufbau ist unabhängig von der Karte in der Hand – einmal je Kontext reicht.
  const rowsByContext: Record<PreviewSectionContext, PreviewRow[]> = useMemo(
    () => ({
      classes: buildRows('classes', store.classes.values(), store.teachers.values(), store.rooms.values()),
      teachers: buildRows('teachers', store.classes.values(), store.teachers.values(), store.rooms.values()),
      rooms: buildRows('rooms', store.classes.values(), store.teachers.values(), store.rooms.values()),
    }),
    [store.classes, store.teachers, store.rooms],
  );

  const sectionData: SectionData[] = (() => {
    const day = pickedCard ? store.placedCardsDay.get(pickedCard.uuid) : undefined;
    const hourUuids = pickedCard ? (store.placedCardsTimeGridEntries.get(pickedCard.uuid) ?? []) : [];

    return sections.map((sectionContext) => {
      const rows = rowsByContext[sectionContext];

      if (!pickedCard) {
        return {
          context: sectionContext,
          rows,
          pickedResources: new Set<string>(),
          pickedCells: new Set<string>(),
          markedCells: new Map<string, 'previous' | 'next'>(),
          notes: [],
        };
      }

      // Zeilen der Karte in der Hand im jeweiligen Kontext → Highlight.
      const pickedResources =
        sectionContext === 'teachers'
          ? new Set(pickedCard.teachers.map((teacher) => teacher.uuid))
          : sectionContext === 'rooms'
            ? new Set(
                (() => {
                  const placedRooms = Array.from(store.cardRooms.get(pickedCard.uuid)?.values() ?? []).map(
                    (room) => room.value,
                  );
                  return placedRooms.length > 0 ? placedRooms : pickedCard.rooms.map((room) => room.uuid);
                })(),
              )
            : new Set(pickedCard.classes.map((schoolClass) => schoolClass.uuid));

      // Fremde Zeilen ausblenden: nur die Lehrkräfte bzw. der Raumvorrat DIESER Karte bleiben
      // sichtbar. Im Lehrer-Board zeigt der Klassen-Abschnitt den ganzen Wirkungskreis der
      // Lehrkraft, sonst deren Klassen auf dieser Karte. Ohne Automatik bleibt der Klassen-Kontext
      // wie bisher ungefiltert.
      let allowed: Set<string> | null = null;
      if (sectionContext === 'teachers') {
        allowed = pickedResources;
      } else if (sectionContext === 'rooms') {
        allowed = cardRoomSupply(
          pickedCard.uuid,
          pickedCard.rooms.map((room) => room.uuid),
          store.defaultCardRooms,
        );
      } else if (autoActive) {
        allowed =
          store.context === 'teachers'
            ? teacherClassUuids(
                new Set(pickedCard.teachers.map((teacher) => teacher.uuid)),
                store.cardTeacher,
                store.cardClasses,
              )
            : new Set(pickedCard.classes.map((schoolClass) => schoolClass.uuid));
      }

      // Exakte Slots der Karte: Behält die Karte ihre Steckung (im Räume-Board bleibt Tag + Stunden
      // erhalten, es wird nur der Raum getauscht), lässt sich die genaue Vorschau-Position
      // markieren. Ohne Steckung (Klassen-/Lehrer-Board) bleibt es beim Zeilen-Highlight.
      const pickedCells = new Set<string>();
      if (day && hourUuids.length > 0) {
        pickedResources.forEach((resourceUuid) => {
          hourUuids.forEach((hourUuid) => pickedCells.add(`${hourUuid}:${day}:${resourceUuid}`));
        });
      }

      // Wegezeiten: im Räume-Board zeigen wir im Räume-Abschnitt, in welchen Räumen die Klassen der
      // Karte unmittelbar davor bzw. danach sind – als Textzeilen, dazu die Zelle im Raster markiert.
      const adjacent =
        sectionContext === 'rooms' && store.context === 'rooms' && day && hourUuids.length > 0
          ? adjacentRoomCells({
              classUuids: pickedCard.classes.map((schoolClass) => schoolClass.uuid),
              day,
              hourUuids,
              previewHours,
              cardsBySlotAndResource: store.cardsBySlotAndResource,
              cardRooms: store.cardRooms,
              pickedCardUuid: pickedCard.uuid,
            })
          : null;

      const roomNames = (uuids: string[]) =>
        uuids
          .map((uuid) => store.rooms.get(uuid)?.name)
          .filter(Boolean)
          .join(', ');

      const notes: string[] = [];
      if (adjacent?.previousRoomUuids.length) {
        notes.push(t('pinboard.previewMap.previousRoom', { room: roomNames(adjacent.previousRoomUuids) }));
      }
      if (adjacent?.nextRoomUuids.length) {
        notes.push(t('pinboard.previewMap.nextRoom', { room: roomNames(adjacent.nextRoomUuids) }));
      }

      return {
        context: sectionContext,
        rows: allowed ? rows.filter((row) => allowed.has(row.uuid)) : rows,
        pickedResources,
        pickedCells,
        markedCells: adjacent?.cells ?? new Map<string, 'previous' | 'next'>(),
        notes,
      };
    });
  })();

  const isEmpty = sectionData.every((section) => section.rows.length === 0);

  return (
    <div className={styles['preview-map']}>
      <div className={styles.header}>
        <div className={styles.title}>{t('pinboard.previewMap.title')}</div>
        <div className={styles.switch}>
          <Button
            hierarchy={store.previewMapAuto ? 'secondary' : 'tertiary'}
            height='s'
            onClick={() => store.setPreviewMapAuto(true)}
          >
            {t('pinboard.previewMap.auto')}
          </Button>
          {CONTEXTS.map((ctx) => (
            <Button
              key={ctx}
              hierarchy={!store.previewMapAuto && ctx === context ? 'secondary' : 'tertiary'}
              height='s'
              onClick={() => store.setPreviewMapContext(ctx)}
            >
              {contextLabels[ctx]}
            </Button>
          ))}
        </div>
      </div>

      <div className={styles['scroll-area']}>
        <div
          className={styles.grid}
          style={{
            gridTemplateColumns: `var(--preview-map-name-width) repeat(${store.days.length * previewHours.length}, minmax(6px, 1fr))`,
          }}
        >
          <div className={styles.corner} />
          {store.days.map((day) => (
            <div key={`day-${day.value}`} className={styles.day} style={{ gridColumn: `span ${previewHours.length}` }}>
              {t(dayToTranslation(day.value))}
            </div>
          ))}

          {sectionData.map((section) => (
            <PreviewSection
              key={section.context}
              context={section.context}
              rows={section.rows}
              pickedResources={section.pickedResources}
              pickedCells={section.pickedCells}
              markedCells={section.markedCells}
              notes={section.notes}
              title={autoActive ? contextLabels[section.context] : undefined}
              hours={previewHours}
            />
          ))}
        </div>
        {isEmpty && <div className={styles.empty}>{t('pinboard.previewMap.empty')}</div>}
      </div>
    </div>
  );
});

PreviewMap.displayName = 'PreviewMap';
